import {memo} from 'react';

import styled from 'styled-components';
import {Dispatch} from 'redux';

import {getLocalizationService} from '~/shared/services/localisationService';
import {flipOnLTR, media} from '~/shared/theme/utils';
import {attachModal} from '~/shared/components/ReduxModal';
import {PopupModalBase} from '~/shared/components/Modals';
import ImageWithAlt from '~/shared/components/ImageWithAlt';
import {bodyRegular, headingS, headingXS, smallRegular} from '~/shared/theme/typography';
import PieButton from '~/shared/theme/pie/PieButton';
import {PieCloseModalButton, PieModalHeader} from '~/shared/components/Modals/styled';
import {setCurrentModal} from '~/shared/store/actions';
import OrderData from '~/shared/store/models/Order/OrderData';

import bagFeesImgSrc from './bagFeesImg.png';

const Root = styled(PopupModalBase)`
  background-color: ${({theme}) => theme.colors.surface};

  &&& {
    background-color: ${({theme}) => theme.colors.surface};
    width: 100%;
    height: auto;
  }

  ${media.minLargeMobile`
    &&& {
      width: 500px;
    }
  `}
`;

const StyledPieCloseModalButton = styled(PieCloseModalButton)`
  top: 16px;
  ${flipOnLTR`
    left: 16px;
  `}
`;

const ContentContainer = styled.div`
  padding: 8px 16px 29px;
  display: flex;
  flex-direction: column;
  align-items: center;
  color: ${({theme}) => theme.colors.contentDefault};
  ${media.minLargeMobile`
    padding: 32px 32px 24px;
  `}
`;

const ImageAndTextsContainer = styled(ContentContainer)`
  width: 100%;
  padding: 0 8px;
  ${media.minLargeMobile`
    padding: 0;
  `}
`;

const Title = styled.h2`
  ${headingXS}
  margin-top: 24px;
  text-align: center;
  ${media.minLargeMobile`
    ${headingS}
  `}
`;

const Subtitle = styled.span`
  ${smallRegular}
  margin-top: 8px;
  text-align: center;
  ${media.minLargeMobile`
    ${bodyRegular}
  `}
`;

const Image = styled(ImageWithAlt).attrs({
  src: bagFeesImgSrc,
  alt: 'Checkout error image',
})`
  width: 150px;
  height: 150px;
  ${media.minLargeMobile`
    width: 200px;
    height: 200px;
  `}
`;

const ActionsContainer = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  gap: 16px;
  margin-top: 32px;
  ${media.minLargeMobile`
    flex-direction: row;
  `}
`;

const ActionButton = styled(PieButton)`
  padding: 10px 16px;
  flex: 1;
  ${media.minLargeMobile`
    padding: 12px 16px;
  `}
`;

type ModalArgs = {
  title: string;
  subtitle?: string;
  showServiceRepresentativeLink: boolean;
  onSuccess?: (permits: OrderData['permits']) => void;
  dispatch: Dispatch;
};

const MainContent = ({title, subtitle, showServiceRepresentativeLink, closeModal, onCodeClick}: {
  title: ModalArgs['title'];
  subtitle: ModalArgs['subtitle'];
  showServiceRepresentativeLink: ModalArgs['showServiceRepresentativeLink'];
  closeModal: () => void;
  onCodeClick: () => void;
}) => {
  const {t} = getLocalizationService();

  return (
    <>
      <PieModalHeader hideOnMobile />
      <ContentContainer id="modal-content">
        <ImageAndTextsContainer>
          <Image />
          <Title id="modal-title">{title}</Title>
          {subtitle && <Subtitle>{subtitle}</Subtitle>}
        </ImageAndTextsContainer>
        <ActionsContainer>
          <ActionButton variant="primaryXL" onClick={closeModal}>
            {t('back_to_menu')}
          </ActionButton>
          {showServiceRepresentativeLink && (
            <ActionButton variant="secondaryXL" onClick={onCodeClick}>
              {t('i_have_a_code_from_customer_service')}
            </ActionButton>
          )}
        </ActionsContainer>
      </ContentContainer>
    </>
  );
};

type CheckoutErrorMessageModalProps = {
  args: ModalArgs;
  closeModal: () => void;
};

const CheckoutErrorMessageWithCodeModal = ({
  args,
  closeModal,
}: CheckoutErrorMessageModalProps) => {
  const onCodeClick = () => args.dispatch(setCurrentModal('service_code_input_modal', {onSuccess: args?.onSuccess}));

  return (
    <Root>
      <StyledPieCloseModalButton onClick={closeModal} hideOnMobile />
      <MainContent
        title={args.title}
        subtitle={args.subtitle}
        showServiceRepresentativeLink={args.showServiceRepresentativeLink}
        closeModal={closeModal}
        onCodeClick={onCodeClick}
      />
    </Root>
  );
};

CheckoutErrorMessageWithCodeModal.mobileBottomSheet = true;
CheckoutErrorMessageWithCodeModal.hideCloseButton = true;
attachModal('checkout_error_message_with_code_modal', CheckoutErrorMessageWithCodeModal);

export default memo(CheckoutErrorMessageWithCodeModal);
